import { useQuery } from "@tanstack/react-query";
import { listVendors, listCategories, listProducts } from "@/lib/api";
import { Store, FolderTree, Package } from "lucide-react";

interface StatCardProps {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  description?: string;
}

function StatCard({ title, value, icon, description }: StatCardProps) {
  return (
    <div className="rounded-lg bg-white p-6 shadow-sm">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="mt-2 text-3xl font-semibold">{value}</p>
          {description && (
            <p className="mt-1 text-sm text-gray-500">{description}</p>
          )}
        </div>
        <div className="rounded-lg bg-gray-100 p-3">{icon}</div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { data: vendors, isLoading: vendorsLoading } = useQuery({
    queryKey: ["vendors"],
    queryFn: () => listVendors({ limit: 100 }),
  });

  const { data: categories, isLoading: categoriesLoading } = useQuery({
    queryKey: ["categories"],
    queryFn: () => listCategories({ limit: 100 }),
  });

  const { data: products, isLoading: productsLoading } = useQuery({
    queryKey: ["products"],
    queryFn: () => listProducts({ limit: 100 }),
  });

  const isLoading = vendorsLoading || categoriesLoading || productsLoading;

  return (
    <div>
      <h1 className="mb-8 text-2xl font-bold">Dashboard</h1>

      {isLoading ? (
        <div className="text-gray-500">Loading...</div>
      ) : (
        <>
          <div className="grid gap-6 md:grid-cols-3">
            <StatCard
              title="Total Vendors"
              value={vendors?.total ?? 0}
              icon={<Store className="h-6 w-6 text-gray-600" />}
              description={`${vendors?.items.filter((v) => v.active).length ?? 0} active`}
            />
            <StatCard
              title="Total Categories"
              value={categories?.total ?? 0}
              icon={<FolderTree className="h-6 w-6 text-gray-600" />}
              description={`${categories?.items.filter((c) => c.active).length ?? 0} active`}
            />
            <StatCard
              title="Total Products"
              value={products?.total ?? 0}
              icon={<Package className="h-6 w-6 text-gray-600" />}
              description={`${products?.items.filter((p) => p.featured).length ?? 0} featured`}
            />
          </div>

          <div className="mt-8 grid gap-6 md:grid-cols-2">
            {/* Recent Vendors */}
            <div className="rounded-lg bg-white p-6 shadow-sm">
              <h2 className="mb-4 text-lg font-semibold">Recent Vendors</h2>
              {vendors?.items.length === 0 ? (
                <p className="text-gray-500">No vendors yet</p>
              ) : (
                <ul className="space-y-3">
                  {vendors?.items.slice(0, 5).map((vendor) => (
                    <li
                      key={vendor.id}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        {vendor.logo ? (
                          <img
                            src={`/api/uploads/${vendor.logo}`}
                            alt={vendor.name}
                            className="h-8 w-8 rounded object-cover"
                          />
                        ) : (
                          <div className="flex h-8 w-8 items-center justify-center rounded bg-gray-100 text-sm font-medium">
                            {vendor.name[0]}
                          </div>
                        )}
                        <span className="font-medium">{vendor.name}</span>
                      </div>
                      <span
                        className={`rounded-full px-2 py-1 text-xs ${
                          vendor.active
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {vendor.active ? "Active" : "Inactive"}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Recent Products */}
            <div className="rounded-lg bg-white p-6 shadow-sm">
              <h2 className="mb-4 text-lg font-semibold">Recent Products</h2>
              {products?.items.length === 0 ? (
                <p className="text-gray-500">No products yet</p>
              ) : (
                <ul className="space-y-3">
                  {products?.items.slice(0, 5).map((product) => (
                    <li
                      key={product.id}
                      className="flex items-center justify-between"
                    >
                      <div>
                        <p className="font-medium">{product.name}</p>
                        <p className="text-sm text-gray-500">
                          {product.vendor?.name} | {product.category?.name}
                        </p>
                      </div>
                      {product.price && (
                        <span className="font-medium">
                          {product.currency} {product.price.toLocaleString()}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
